import { Box, Typography, type Theme } from '@mui/material';
import Color from 'color';
import { CheckBox } from '../../../../../workflows/quotes/prime-x/shared/helpers';
import type { RaterXCoverage } from '../../../../../workflows/quotes/prime-x/api/types/rater-x-preset';
import { isMandatoryCoverage } from '../../../../../workflows/quotes/prime-x/shared/coverageLock';
import { deriveCurrencyFormat } from '../../../../../../i18n/currencies';
import type { Languages } from '../../../../../../i18n/i18n.language-config';
import { toUniversalUtcDate } from '../../../../../../utils/date.utils';
import { retroActiveDateMap } from '../../utils/primeX.util';

interface ICoverageXLineItem {
  raterXCoverage: RaterXCoverage;
  language: Languages;
  isOdd: boolean;
}
const CoverageXLineItem = ({
  raterXCoverage,
  language,
  isOdd,
}: ICoverageXLineItem) => {
  const coverageLimit = raterXCoverage?.value?.props?.limit?.value
    ? deriveCurrencyFormat(language, raterXCoverage.value.props.limit.value, {
        minimumFractionDigits: 0,
      })
    : '-';
  const deductible = raterXCoverage?.value?.props?.deductible?.value
    ? deriveCurrencyFormat(
        language,
        raterXCoverage.value.props.deductible.value,
        {
          minimumFractionDigits: 0,
        }
      )
    : '-';
  const waitingPeriod = formatWaitingPeriod(raterXCoverage);
  const retroActiveDate = getRetroActiveDate(raterXCoverage);

  const styleObject = (theme: Theme) => {
    const oddRowBackground = Color(theme.palette.background.default)
      .lighten(theme.palette.mode === 'dark' ? 0.28 : 0.025)
      .toString();

    const evenRowBackground =
      theme.palette.mode === 'light'
        ? theme.palette.background.modal
        : Color(theme.palette.background.modal).darken(0.15).toString();
    return {
      backgroundColor: isOdd ? oddRowBackground : evenRowBackground,
      display: 'grid',
      alignItems: 'center',
      justifyItems: 'end',
      '@container (max-width: 840px)': {
        justifyItems: 'center',
        fontSize: '12px',
      },
    };
  };
  const textStyleObject = {
    '@container (max-width: 840px)': {
      fontSize: '1rem',
      textAlign: 'center',
    },
  };

  return (
    <>
      <Box sx={styleObject}>
        <Box sx={{ justifySelf: 'start' }}>
          <CheckBox
            isAvailable={raterXCoverage.isAvailable}
            isSelected={raterXCoverage.isSelected}
            isMandatory={isMandatoryCoverage(raterXCoverage)}
            tooltip={raterXCoverage.description}
            label={raterXCoverage.fullName}
            disabled
          />
        </Box>
      </Box>
      <Box sx={styleObject}>
        <Typography sx={textStyleObject}>{coverageLimit}</Typography>
      </Box>
      <Box sx={styleObject}>
        <Typography sx={textStyleObject}>{deductible}</Typography>
      </Box>
      <Box sx={styleObject}>
        <Box sx={{ justifySelf: 'center' }}>
          <Typography sx={textStyleObject}>{waitingPeriod}</Typography>
        </Box>
      </Box>
      <Box sx={styleObject}>
        <Box sx={{ justifySelf: 'center' }}>
          <Typography sx={textStyleObject}>{retroActiveDate}</Typography>
        </Box>
      </Box>
    </>
  );
};

const formatWaitingPeriod = (coverageData: RaterXCoverage) => {
  if (!coverageData.isSelected) {
    return '-';
  }

  const waitingPeriod = coverageData.value?.props?.waitingPeriod?.value;

  return waitingPeriod ? `${waitingPeriod} hours` : 'None';
};

const getRetroActiveDate = (coverageData: RaterXCoverage) => {
  const retroActiveDate =
    coverageData?.value?.props?.retroactive?.props?.date?.value;
  if (retroActiveDate) {
    return toUniversalUtcDate(retroActiveDate);
  }

  const retroActivePeriod =
    coverageData?.value?.props?.retroactive?.props?.period?.value;
  if (retroActivePeriod) {
    return retroActiveDateMap[
      retroActivePeriod as keyof typeof retroActiveDateMap
    ];
  }

  return '-';
};

export default CoverageXLineItem;
